/**
 * AuditLogFilters Component
 *
 * Filter bar for the audit log page with resource type, action, and date range controls.
 */

import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { Select } from "../../../components/ui/Select";
import { Input } from "../../../components/ui/Input";
import { Button } from "../../../components/ui/Button";
import type { AuditAction } from "../../../types/audit-log.types";

const RESOURCE_TYPE_OPTIONS = [
  { value: "", label: "All Resources" },
  { value: "USER", label: "User" },
  { value: "APPLICATION", label: "Application" },
  { value: "ROLE", label: "Role" },
  { value: "TENANT_ROLE", label: "Tenant Role" },
  { value: "GROUP", label: "Group" },
  { value: "SECRET", label: "Secret" },
  { value: "VOLUME", label: "Volume" },
  { value: "DATABASE_CONNECTION", label: "Database Connection" },
  { value: "DOCUMENT_DB", label: "Document DB" },
  { value: "MANAGED_SQL_DB", label: "Managed SQL DB" },
  { value: "DEPLOYMENT", label: "Deployment" },
  { value: "DEPLOYMENT_TOKEN", label: "Deployment Token" },
  { value: "SERVICE_ACCOUNT", label: "Service Account" },
  { value: "APP_JOB", label: "App Job" },
  { value: "DATA_AGENT", label: "Data Agent" },
  { value: "ELT_JOB", label: "ELT Job" },
  { value: "INVITATION", label: "Invitation" },
];

const ACTION_OPTIONS = [
  { value: "", label: "All Actions" },
  { value: "CREATE", label: "Create" },
  { value: "UPDATE", label: "Update" },
  { value: "DELETE", label: "Delete" },
];

export interface AuditLogFilterValues {
  resourceType: string;
  action: string;
  startDate: string;
  endDate: string;
}

interface AuditLogFiltersProps {
  filters: AuditLogFilterValues;
  onFilterChange: (filters: AuditLogFilterValues) => void;
  onRefresh: () => void;
  isFetching: boolean;
  totalCount: number;
  displayedCount: number;
}

export const AuditLogFilters: React.FC<AuditLogFiltersProps> = ({
  filters,
  onFilterChange,
  onRefresh,
  isFetching,
  totalCount,
  displayedCount,
}) => {
  const updateFilter = (key: keyof AuditLogFilterValues, value: string) => {
    onFilterChange({ ...filters, [key]: value });
  };

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="w-48">
        <Select value={filters.resourceType} onChange={(e) => updateFilter("resourceType", e.target.value)}>
          {RESOURCE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Select>
      </div>
      <div className="w-40">
        <Select value={filters.action} onChange={(e) => updateFilter("action", e.target.value as AuditAction | "")}>
          {ACTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Select>
      </div>
      <div className="w-40">
        <Input
          type="date"
          value={filters.startDate}
          onChange={(e) => updateFilter("startDate", e.target.value)}
          placeholder="Start date"
        />
      </div>
      <div className="w-40">
        <Input
          type="date"
          value={filters.endDate}
          onChange={(e) => updateFilter("endDate", e.target.value)}
          placeholder="End date"
        />
      </div>
      <Button variant="secondary" size="sm" onClick={onRefresh} disabled={isFetching}>
        <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? "animate-spin" : ""}`} />
        Refresh
      </Button>
      <div className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
        Showing {displayedCount} of {totalCount} entries
      </div>
    </div>
  );
};
